import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { logger } from '../../lib/logger';
import { BugReportDialog } from './BugReportDialog';
import { captureFullPage } from './captureFullPage';

/**
 * Der Melde-Knopf saß bis hierher ausschließlich in der Kopfzeile.
 * Sobald ein Popup offen war, lag der Grauschleier darüber und der Knopf war
 * nicht mehr erreichbar — ausgerechnet dort, wo die meisten Fehler auftreten.
 *
 * Der Knopf steht jetzt zusätzlich in der Titelzeile jedes Popups (siehe
 * `components/common/AppDialogTitle.tsx`). Damit gibt es viele Knöpfe, aber
 * es darf nur EINEN Melde-Vorgang geben: Bildschirmabzug, Melde-Dialog und
 * Rückmeldungen liegen deshalb hier im Anbieter und existieren genau einmal.
 * Hätte jede Titelzeile je eine eigene Instanz, gäbe es dutzende parallele
 * Abzüge und Dialoge.
 */

/**
 * SMSPRO-111 — der Bildschirmabzug dauert auf großen Seiten mehrere Sekunden.
 *
 * Messung (Chromium, 1920x1080, synthetische Changelog-Seite mit 9.704 Knoten):
 *   toPng gesamt ~10,5 s — davon ~75 % Klonen des DOM inkl. Kopie der
 *   berechneten Stile pro Knoten (Chrome liefert computedStyle.cssText leer,
 *   html-to-image kopiert daher 456 Einzel-Properties je Knoten), ~25 %
 *   Dekodieren des daraus entstehenden ~76 MB großen SVG. PNG-Kodierung
 *   selbst: 14 ms. Der Aufwand hängt an der KNOTENZAHL, nicht an der
 *   Bildgröße. Seit SMSPRO-183 umfasst der Abzug die ganze Seite; die
 *   Knotenzahl bleibt dabei unverändert, die Dauer also ebenfalls.
 *
 * Wichtig für die Rückmeldung: der Hauptthread blockiert dabei in wenigen
 * großen Blöcken (gemessen: 5,7 s und 4,4 s am Stück). Eine Ladeanzeige
 * animiert währenddessen nicht. Sie muss deshalb GEZEICHNET sein, bevor die
 * Aufnahme startet — daher das bewusste Warten auf zwei Frames.
 */
async function waitForPaint(): Promise<void> {
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export interface BugReportContextValue {
  /** Startet Bildschirmabzug und öffnet danach den Melde-Dialog. */
  starteMeldung: () => void;
  /** Läuft gerade eine Aufnahme? (Knöpfe sperren, solange sie läuft.) */
  aufnahmeLaeuft: boolean;
}

const BugReportContext = createContext<BugReportContextValue | null>(null);

/**
 * Zugriff auf den Melde-Ablauf.
 *
 * Gibt bewusst `null` zurück, wenn kein Anbieter darüber hängt, statt zu
 * werfen: Popups werden in Tests und in Teilbäumen ohne Anwendungswurzel
 * gerendert. Die Titelzeile blendet den Knopf dann einfach aus, statt den
 * ganzen Baum scheitern zu lassen.
 */
export function useBugReport(): BugReportContextValue | null {
  return useContext(BugReportContext);
}

export function BugReportProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  // Zweite Sperre neben `disabled`: ein zweiter Klick im selben Frame darf
  // keine zweite Aufnahme starten (der State ist dann noch nicht übernommen).
  // Sie wirkt jetzt zusätzlich über ALLE Knöpfe hinweg — Kopfzeile wie
  // Popup-Titelzeile teilen sich diese eine Sperre.
  const capturingRef = useRef(false);

  const starteMeldung = useCallback(async () => {
    if (capturingRef.current) return;
    capturingRef.current = true;
    setCapturing(true);
    setScreenshotError(null);

    // Fortschrittsanzeige zuerst zeichnen lassen, danach erst die blockierende Arbeit.
    await waitForPaint();

    const startedAt = performance.now();
    try {
      // SMSPRO-183 — ganze Seite statt nur des Sichtfensters (siehe captureFullPage.ts).
      // Ein offenes Popup hängt als Portal unter document.body und ist damit
      // Teil des Abzugs; nur der Grauschleier ist per Filter ausgenommen.
      const { dataUrl, width, height, pixelRatio } = await captureFullPage();
      setScreenshotDataUrl(dataUrl);
      logger.info('ui', 'bug report screenshot captured', {
        ms: Math.round(performance.now() - startedAt),
        bytes: dataUrl.length,
        nodes: document.querySelectorAll('*').length,
        pageWidth: width,
        pageHeight: height,
        pixelRatio: Math.round(pixelRatio * 100) / 100,
        route: window.location.pathname,
        // Belegt in der Meldung, ob beim Abzug ein Popup offen war.
        dialogOffen: document.querySelectorAll('[role="dialog"]').length,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setScreenshotDataUrl(null);
      setScreenshotError(msg);
      logger.error('error', 'bug report screenshot failed', {
        error: msg,
        ms: Math.round(performance.now() - startedAt),
        route: window.location.pathname,
      });
    } finally {
      capturingRef.current = false;
      setCapturing(false);
      setDialogOpen(true);
    }
  }, []);

  const value = useMemo<BugReportContextValue>(
    () => ({ starteMeldung: () => void starteMeldung(), aufnahmeLaeuft: capturing }),
    [starteMeldung, capturing],
  );

  return (
    <BugReportContext.Provider value={value}>
      {children}

      {/* Sofort sichtbare Rückmeldung während der Aufnahme. Ohne Transition,
          damit sie im ersten Frame vollständig steht; per data-Attribut vom
          Bildschirmabzug ausgenommen, sonst würde sie mit aufgenommen. */}
      <Snackbar
        open={capturing}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transitionDuration={0}
        data-bug-report-progress=""
      >
        <Alert severity="info" variant="filled" icon={false} sx={{ minWidth: 320 }}>
          <AlertTitle sx={{ mb: 0.5 }}>
            {t('bugReport.capturing', 'Bildschirmabzug wird erstellt…')}
          </AlertTitle>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            {t('bugReport.capturingHint', 'Bei sehr langen Seiten dauert das einige Sekunden.')}
          </Typography>
          <Box sx={{ width: '100%' }}>
            <LinearProgress color="inherit" />
          </Box>
        </Alert>
      </Snackbar>

      {/* Der EINE Melde-Dialog. Er wird nach dem bereits offenen Popup an
          document.body gehängt und liegt deshalb darüber — belegt in
          __tests__/bugReportImPopup.test.tsx. */}
      <BugReportDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        screenshotDataUrl={screenshotDataUrl}
        screenshotError={screenshotError}
        onSuccess={() => setSuccessOpen(true)}
      />

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessOpen(false)} severity="success" variant="filled">
          {t('bugReport.success')}
        </Alert>
      </Snackbar>
    </BugReportContext.Provider>
  );
}
